import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { useAuthStore } from '../stores/authStore';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';
const API_TIMEOUT = import.meta.env.VITE_API_TIMEOUT ? parseInt(import.meta.env.VITE_API_TIMEOUT) : 30000;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: API_TIMEOUT,
});

// Token refresh state management - prevents multiple simultaneous refresh requests
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach(promise => {
    if (error) {
      promise.reject(error);
    } else if (token) {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    const { accessToken } = useAuthStore.getState();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle token refresh and errors
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Check if this is a login or refresh request - don't retry those
      if (originalRequest.url?.includes('/auth/login') ||
          originalRequest.url?.includes('/auth/refresh-token')) {
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              resolve(api(originalRequest));
            },
            reject: (err: Error) => {
              reject(err);
            }
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const { refreshToken, logout, setTokens } = useAuthStore.getState();

      if (refreshToken) {
        try {
          // Attempt to refresh token
          const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
            refreshToken,
          });

          const { accessToken: newAccessToken } = response.data.data;
          setTokens(newAccessToken);

          // Process queued requests with new token
          processQueue(null, newAccessToken);

          // Retry original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          }
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, process queue with error and logout
          processQueue(refreshError as Error, null);
          logout();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      } else {
        isRefreshing = false;
        logout();
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// API Types
export interface ApiResponse<T = unknown> {
  success: boolean;
  message?: string;
  data?: T;
  pagination?: {
    page: number;
    limit: number;
    totalRecords: number;
    totalPages: number;
  };
  errors?: Array<{
    field: string;
    message: string;
  }>;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

// Auth API
export const authApi = {
  login: (credentials: { email?: string; username?: string; password: string }) =>
    api.post<ApiResponse>('/auth/login', credentials),
  
  logout: () => api.post<ApiResponse>('/auth/logout'),
  
  verifyMFA: (data: { mfaToken: string; otpCode: string }) =>
    api.post<ApiResponse>('/auth/verify-mfa', data),
  
  setupMFA: () => api.post<ApiResponse>('/auth/setup-mfa'),
  
  enableMFA: (otpCode: string) =>
    api.post<ApiResponse>('/auth/enable-mfa', { otpCode }),
  
  disableMFA: (data: { password: string; otpCode: string }) =>
    api.post<ApiResponse>('/auth/disable-mfa', data),
  
  changePassword: (data: { currentPassword: string; newPassword: string; confirmPassword: string }) =>
    api.post<ApiResponse>('/auth/change-password', data),
  
  forgotPassword: (email: string) =>
    api.post<ApiResponse>('/auth/forgot-password', { email }),
  
  resetPassword: (data: { token: string; newPassword: string; confirmPassword: string }) =>
    api.post<ApiResponse>('/auth/reset-password', data),
  
  getMe: () => api.get<ApiResponse>('/auth/me'),
  
  getSessions: () => api.get<ApiResponse>('/auth/sessions'),
  
  terminateSession: (sessionId: string) =>
    api.delete<ApiResponse>(`/auth/sessions/${sessionId}`),
  
  refreshToken: (refreshToken: string) =>
    api.post<ApiResponse>('/auth/refresh-token', { refreshToken }),
};

// Users API
export const usersApi = {
  getAll: (params?: PaginationParams & { roleId?: string }) =>
    api.get<ApiResponse>('/users', { params }),
  
  getById: (id: string) => api.get<ApiResponse>(`/users/${id}`),
  
  getRoles: () => api.get<ApiResponse>('/roles'),
  
  create: (data: Record<string, unknown>) =>
    api.post<ApiResponse>('/users', data),
  
  update: (id: string, data: Record<string, unknown>) =>
    api.put<ApiResponse>(`/users/${id}`, data),
  
  delete: (id: string) => api.delete<ApiResponse>(`/users/${id}`),
  
  lock: (id: string, data?: { reason?: string; duration?: number }) =>
    api.post<ApiResponse>(`/users/${id}/lock`, data),
  
  unlock: (id: string) => api.post<ApiResponse>(`/users/${id}/unlock`),
  
  activate: (id: string) => api.post<ApiResponse>(`/users/${id}/activate`),
  
  deactivate: (id: string) => api.post<ApiResponse>(`/users/${id}/deactivate`),
  
  resetPassword: (id: string) =>
    api.post<ApiResponse>(`/users/${id}/reset-password`),
};

// Roles API
export const rolesApi = {
  getAll: () => api.get<ApiResponse>('/roles'),
  
  getById: (id: string) => api.get<ApiResponse>(`/roles/${id}`),
  
  create: (data: Record<string, unknown>) =>
    api.post<ApiResponse>('/roles', data),
  
  update: (id: string, data: Record<string, unknown>) =>
    api.put<ApiResponse>(`/roles/${id}`, data),
  
  delete: (id: string) => api.delete<ApiResponse>(`/roles/${id}`),
  
  getPermissions: () => api.get<ApiResponse>('/roles/permissions'),
};

// Assets API
export const assetsApi = {
  getAll: (params?: PaginationParams & {
    status?: string;
    assetClass?: string;
    plant?: string;
    location?: string;
    company?: string;
    includeRetired?: boolean;
  }) => api.get<ApiResponse>('/assets', { params }),
  
  getById: (id: string) => api.get<ApiResponse>(`/assets/${id}`),
  
  getByQR: (qrCode: string) => api.get<ApiResponse>(`/assets/qr/${qrCode}`),
  
  create: (data: Record<string, unknown>, imageFile?: File) => {
    const formData = new FormData();
    // Append all data fields directly to FormData
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined && data[key] !== null) {
        formData.append(key, String(data[key]));
      }
    });
    if (imageFile) {
      formData.append('image', imageFile);
    }
    return api.post<ApiResponse>('/assets', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  update: (id: string, data: Record<string, unknown>, imageFile?: File) => {
    const formData = new FormData();
    // Append all data fields directly to FormData
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined && data[key] !== null) {
        formData.append(key, String(data[key]));
      }
    });
    if (imageFile) {
      formData.append('image', imageFile);
    }
    return api.put<ApiResponse>(`/assets/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  delete: (id: string, reason?: string) =>
    api.delete<ApiResponse>(`/assets/${id}`, { data: { reason } }),
  
  changeStatus: (id: string, status: string, reason?: string) =>
    api.patch<ApiResponse>(`/assets/${id}/status`, { status, reason }),
  
  retire: (id: string, reason: string, retirementDate?: string) =>
    api.post<ApiResponse>(`/assets/${id}/retire`, { reason, retirementDate }),
  
  getStatistics: (params?: { plant?: string; company?: string }) =>
    api.get<ApiResponse>('/assets/statistics', { params }),
  
  search: (query: string, filters?: Record<string, unknown>, pagination?: PaginationParams) =>
    api.post<ApiResponse>('/assets/search', { query, filters, ...pagination }),
};

// Locations API
export const locationsApi = {
  getAll: (params?: { type?: string; parentId?: string; flat?: 'true' | 'false' | boolean }) =>
    api.get<ApiResponse>('/locations', { params }),
  
  getById: (id: string) => api.get<ApiResponse>(`/locations/${id}`),
  
  create: (data: Record<string, unknown>) =>
    api.post<ApiResponse>('/locations', data),
  
  update: (id: string, data: Record<string, unknown>) =>
    api.put<ApiResponse>(`/locations/${id}`, data),
  
  delete: (id: string) => api.delete<ApiResponse>(`/locations/${id}`),
  
  getStatistics: () => api.get<ApiResponse>('/locations/statistics'),
  
  getCompanyDetails: (companyId?: string) => 
    api.get<ApiResponse>('/locations/company-details', { params: { company_id: companyId } }),
};

// Transfers API
export const transfersApi = {
  getAll: (params?: PaginationParams & {
    status?: string;
    fromDate?: string;
    toDate?: string;
    assetId?: string;
  }) => api.get<ApiResponse>('/transfers', { params }),
  
  getById: (id: string) => api.get<ApiResponse>(`/transfers/${id}`),
  
  getByAsset: (assetId: string) =>
    api.get<ApiResponse>(`/transfers/asset/${assetId}`),
  
  create: (data: Record<string, unknown>) =>
    api.post<ApiResponse>('/transfers', data),
  
  createBulk: (data: {
    assetIds: string[];
    fromLocationId: string;
    toLocationId: string;
    reason?: string;
    requiresApproval?: boolean;
  }) => api.post<ApiResponse>('/transfers/bulk', data),
  
  approve: (id: string, notes?: string) =>
    api.post<ApiResponse>(`/transfers/${id}/approve`, { notes }),
  
  reject: (id: string, reason?: string) =>
    api.post<ApiResponse>(`/transfers/${id}/reject`, { reason }),
  
  complete: (id: string, notes?: string) =>
    api.post<ApiResponse>(`/transfers/${id}/complete`, { notes }),
  
  cancel: (id: string, reason?: string) =>
    api.post<ApiResponse>(`/transfers/${id}/cancel`, { reason }),
};

// Audit API
export const auditApi = {
  getAll: (params?: PaginationParams & {
    action?: string;
    entityType?: string;
    userId?: string;
    assetId?: string;
    fromDate?: string;
    toDate?: string;
    startDate?: string;
    endDate?: string;
  }) => api.get<ApiResponse>('/audit', { params }),
  
  getById: (id: string) => api.get<ApiResponse>(`/audit/${id}`),
  
  getByEntity: (entityType: string, entityId: string, params?: PaginationParams) =>
    api.get<ApiResponse>(`/audit/entity/${entityType}/${entityId}`, { params }),
  
  getByAsset: (assetId: string, params?: PaginationParams) =>
    api.get<ApiResponse>(`/audit/asset/${assetId}`, { params }),
  
  getByUser: (userId: string, params?: PaginationParams) =>
    api.get<ApiResponse>(`/audit/user/${userId}`, { params }),
  
  getStatistics: (params?: { fromDate?: string; toDate?: string }) =>
    api.get<ApiResponse>('/audit/statistics', { params }),
  
  getSecurityLogs: (params?: PaginationParams & { fromDate?: string; toDate?: string }) =>
    api.get<ApiResponse>('/audit/security', { params }),
  
  export: (data: {
    format: 'csv' | 'xlsx' | 'json' | 'excel' | 'pdf';
    action?: string;
    entityType?: string;
    userId?: string;
    assetId?: string;
    fromDate?: string;
    toDate?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
  }) => api.post('/audit/export', data, { responseType: 'blob' }),
};

// Reports API
export const reportsApi = {
  getDashboard: () => api.get<ApiResponse>('/reports/dashboard'),
  
  generate: (reportId: string, params?: Record<string, unknown>) =>
    api.post(`/reports/${reportId}`, params, { responseType: 'blob' }),
  
  getInventory: (params?: Record<string, unknown>) =>
    api.get<ApiResponse>('/reports/inventory', { params }),
  
  getTransfers: (params?: Record<string, unknown>) =>
    api.get<ApiResponse>('/reports/transfers', { params }),
  
  getMaintenance: (params?: Record<string, unknown>) =>
    api.get<ApiResponse>('/reports/maintenance', { params }),
  
  getFinancial: (params?: Record<string, unknown>) =>
    api.get<ApiResponse>('/reports/financial', { params }),
  
  getAudit: (params?: Record<string, unknown>) =>
    api.get<ApiResponse>('/reports/audit', { params }),
  
  exportPDF: (data: { reportType: string; filters?: Record<string, unknown> }) =>
    api.post('/reports/export/pdf', data, { responseType: 'blob' }),
  
  exportExcel: (data: { reportType: string; filters?: Record<string, unknown> }) =>
    api.post('/reports/export/excel', data, { responseType: 'blob' }),
  
  exportCSV: (data: { reportType: string; filters?: Record<string, unknown> }) =>
    api.post('/reports/export/csv', data, { responseType: 'blob' }),
};

// Asset Audit API
export const assetAuditApi = {
  getAll: (params?: PaginationParams & {
    assetId?: string;
    status?: string;
    fromDate?: string;
    toDate?: string;
  }) => api.get<ApiResponse>('/asset-audits', { params }),
  
  getByAsset: (assetId: string) =>
    api.get<ApiResponse>(`/asset-audits/asset/${assetId}`),
  
  getStats: (params?: { fromDate?: string; toDate?: string }) =>
    api.get<ApiResponse>('/asset-audits/stats', { params }),
  
  create: (data: {
    assetId: string;
    auditType?: string;
    status: string;
    condition?: string;
    locationVerified?: boolean;
    notes?: string;
    scannedQrCode?: string;
    image?: File;
  }) => {
    const formData = new FormData();
    // Append all data fields directly to FormData
    Object.keys(data).forEach(key => {
      if (key !== 'image' && data[key as keyof typeof data] !== undefined && data[key as keyof typeof data] !== null) {
        formData.append(key, String(data[key as keyof typeof data]));
      }
    });
    if (data.image) {
      formData.append('image', data.image);
    }
    return api.post<ApiResponse>('/asset-audits', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// QR API
export const qrApi = {
  generate: (assetId: string, regenerate?: boolean) =>
    api.post<ApiResponse>(`/qr/generate/${assetId}`, { regenerate }),
  
  generateBulk: (assetIds: string[], regenerate?: boolean) =>
    api.post<ApiResponse>('/qr/bulk-generate', { assetIds, regenerate }),
  
  get: (assetId: string, format?: 'dataurl' | 'png') =>
    api.get<ApiResponse>(`/qr/${assetId}`, { params: { format } }),
  
  getLabel: (assetId: string, format?: 'zpl' | 'image') =>
    api.get(`/qr/${assetId}/label`, { params: { format }, responseType: format === 'zpl' ? 'text' : 'blob' }),
  
  bulkGenerate: (assetIds: string[], regenerate?: boolean) =>
    api.post<ApiResponse>('/qr/bulk-generate', { assetIds, regenerate }),
  
  export: (assetIds: string[], format?: 'pdf' | 'json') =>
    api.post('/qr/export', { assetIds, format }, { responseType: format === 'pdf' ? 'blob' : 'json' }),
  
  getConfig: () => api.get<ApiResponse>('/qr/config'),
  
  updateConfig: (config: Record<string, unknown>) =>
    api.put<ApiResponse>('/qr/config', config),
};

// Bulk Upload API
export const bulkUploadApi = {
  getTemplate: (type: 'assets' | 'users') =>
    api.get(`/bulk-upload/template/${type}`, { responseType: 'blob' }),
  
  downloadAssetTemplate: () =>
    api.get('/bulk-upload/template/assets', { responseType: 'blob' }),
  
  downloadUserTemplate: () =>
    api.get('/bulk-upload/template/users', { responseType: 'blob' }),
  
  validate: (formData: FormData) =>
    api.post<ApiResponse>('/bulk-upload/validate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  
  import: (formData: FormData) =>
    api.post<ApiResponse>('/bulk-upload/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  
  previewAssets: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<ApiResponse>('/bulk-upload/preview/assets', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  uploadAssets: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<ApiResponse>('/bulk-upload/asset', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  uploadUsers: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<ApiResponse>('/bulk-upload/users', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  getHistory: (params?: PaginationParams) =>
    api.get<ApiResponse>('/bulk-upload/history', { params }),
};

// Masters API
export const mastersApi = {
  // Asset Classes
  getAssetClasses: (activeOnly = true) =>
    api.get<ApiResponse>('/masters/asset-classes', { params: { activeOnly } }),

  getAssetClassById: (id: string) =>
    api.get<ApiResponse>(`/masters/asset-classes/${id}`),

  createAssetClass: (data: { code: string; name: string; description?: string }) =>
    api.post<ApiResponse>('/masters/asset-classes', data),

  updateAssetClass: (id: string, data: { code: string; name: string; description?: string; isActive?: boolean }) =>
    api.put<ApiResponse>(`/masters/asset-classes/${id}`, data),

  deleteAssetClass: (id: string) =>
    api.delete<ApiResponse>(`/masters/asset-classes/${id}`),

  // Vendors
  getVendors: (activeOnly = true) =>
    api.get<ApiResponse>('/masters/vendors', { params: { activeOnly } }),

  getVendorById: (id: string) =>
    api.get<ApiResponse>(`/masters/vendors/${id}`),

  createVendor: (data: Record<string, unknown>) =>
    api.post<ApiResponse>('/masters/vendors', data),

  updateVendor: (id: string, data: Record<string, unknown>) =>
    api.put<ApiResponse>(`/masters/vendors/${id}`, data),

  deleteVendor: (id: string) =>
    api.delete<ApiResponse>(`/masters/vendors/${id}`),

  // Company Codes
  getCompanyCodes: (activeOnly = true) =>
    api.get<ApiResponse>('/masters/company-codes', { params: { activeOnly } }),

  getCompanyCodeById: (id: string) =>
    api.get<ApiResponse>(`/masters/company-codes/${id}`),

  createCompanyCode: (data: { code: string; name: string; description?: string; address?: string }) =>
    api.post<ApiResponse>('/masters/company-codes', data),

  updateCompanyCode: (id: string, data: Record<string, unknown>) =>
    api.put<ApiResponse>(`/masters/company-codes/${id}`, data),

  deleteCompanyCode: (id: string) =>
    api.delete<ApiResponse>(`/masters/company-codes/${id}`),

  // Plants
  getPlants: (activeOnly = true, companyId?: string) =>
    api.get<ApiResponse>('/masters/plants', { params: { activeOnly, companyId } }),

  getPlantById: (id: string) =>
    api.get<ApiResponse>(`/masters/plants/${id}`),

  createPlant: (data: Record<string, unknown>) =>
    api.post<ApiResponse>('/masters/plants', data),

  updatePlant: (id: string, data: Record<string, unknown>) =>
    api.put<ApiResponse>(`/masters/plants/${id}`, data),

  deletePlant: (id: string) =>
    api.delete<ApiResponse>(`/masters/plants/${id}`),

  // Cost Centers
  getCostCenters: (activeOnly = true, companyId?: string) =>
    api.get<ApiResponse>('/masters/cost-centers', { params: { activeOnly, companyId } }),

  getCostCenterById: (id: string) =>
    api.get<ApiResponse>(`/masters/cost-centers/${id}`),

  createCostCenter: (data: Record<string, unknown>) =>
    api.post<ApiResponse>('/masters/cost-centers', data),

  updateCostCenter: (id: string, data: Record<string, unknown>) =>
    api.put<ApiResponse>(`/masters/cost-centers/${id}`, data),

  deleteCostCenter: (id: string) =>
    api.delete<ApiResponse>(`/masters/cost-centers/${id}`),

  // Business Areas
  getBusinessAreas: (activeOnly = true) =>
    api.get<ApiResponse>('/masters/business-areas', { params: { activeOnly } }),

  getBusinessAreaById: (id: string) =>
    api.get<ApiResponse>(`/masters/business-areas/${id}`),

  createBusinessArea: (data: {
    code: string;
    name: string;
    description?: string;
    companyId?: string;
    plant_id?: string;
    latitude?: number;
    longitude?: number;
    address?: string;
  }) =>
    api.post<ApiResponse>('/masters/business-areas', data),

  updateBusinessArea: (id: string, data: {
    code: string;
    name: string;
    description?: string;
    companyId?: string;
    plant_id?: string;
    latitude?: number;
    longitude?: number;
    address?: string;
    isActive?: boolean;
  }) =>
    api.put<ApiResponse>(`/masters/business-areas/${id}`, data),

  deleteBusinessArea: (id: string) =>
    api.delete<ApiResponse>(`/masters/business-areas/${id}`),
};

// Settings API
export const settingsApi = {
  getAll: () => api.get<ApiResponse>('/settings'),
  
  getByCategory: (category: string) =>
    api.get<ApiResponse>(`/settings/${category}`),
  
  update: (category: string, data: Record<string, unknown>) =>
    api.put<ApiResponse>(`/settings/${category}`, data),
  
  getCompanyProfile: () => api.get<ApiResponse>('/settings/company/profile'),
  
  updateCompanyProfile: (data: Record<string, unknown>) =>
    api.put<ApiResponse>('/settings/company/profile', data),
  
  getEmailSettings: () => api.get<ApiResponse>('/settings/email'),
  
  updateEmailSettings: (data: Record<string, unknown>) =>
    api.put<ApiResponse>('/settings/email', data),
  
  testEmail: (testEmail: string) =>
    api.post<ApiResponse>('/settings/email/test', { testEmail }),
  
  getSecuritySettings: () => api.get<ApiResponse>('/settings/security'),
  
  updateSecuritySettings: (data: Record<string, unknown>) =>
    api.put<ApiResponse>('/settings/security', data),
  
  getPreferences: () => api.get<ApiResponse>('/settings/preferences'),
  
  updatePreferences: (data: Record<string, unknown>) =>
    api.put<ApiResponse>('/settings/preferences', data),
};

export default api;

